import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, IndianRupee, Clock, CheckCircle2, AlertTriangle, ArrowUpRight, Building, RefreshCw, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, hasPermission } from '../../constants/permissions';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
  processing: { label: 'Processing', color: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-400' },
  settled: { label: 'Settled', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400' },
  failed: { label: 'Failed', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-400' },
  not_applicable: { label: 'Not applicable', color: 'bg-slate-50 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
};

const modeLabels = {
  razorpay_route: 'Razorpay Route',
  normal_razorpay: 'Normal Razorpay',
  direct_bank: 'Direct Bank',
  pending_route: 'Route Pending',
};

const formatMoney = (value) => `₹${Number(value || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const formatDate = (value) => {
  if (!value) return 'Not available yet';
  return new Date(value).toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const hasLinkedRazorpayAccount = (entry = {}) => Boolean(
  entry.hasLinkedAccount
  || entry.linkedAccountId
  || entry.transferId
);

export default function AdminSettlements({ apiBase = '/api/admin' }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'admin';
  const canRefreshSettlements = hasPermission(user, PERMISSIONS.SETTLEMENTS_REFRESH);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [liveRefresh, setLiveRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterInstitution, setFilterInstitution] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [filterPayoutMode, setFilterPayoutMode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterOptions, setFilterOptions] = useState({ institutions: [], courses: [] });
  const [refreshingId, setRefreshingId] = useState('');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 2000);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    axios.get(`${apiBase}/settlements/filters`)
      .then((res) => setFilterOptions({
        institutions: res.data?.institutions || [],
        courses: res.data?.courses || [],
      }))
      .catch(() => toast.error('Failed to load settlement filters'));
  }, [apiBase]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filterStatus, filterInstitution, filterCourse, filterPayoutMode, dateFrom, dateTo]);

  const fetchSettlements = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
      };
      if (filterStatus) params.status = filterStatus;
      if (debouncedSearch) params.q = debouncedSearch;
      if (filterInstitution) params.institutionId = filterInstitution;
      if (filterCourse) params.courseId = filterCourse;
      if (filterPayoutMode) params.payoutMode = filterPayoutMode;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await axios.get(`${apiBase}/settlements`, { params });
      setEntries(res.data.entries || []);
      setSummary(res.data.summary || {});
      setLiveRefresh(Boolean(res.data.liveRefresh));
      setTotal(res.data.total || 0);
      setTotalPages(Math.ceil((res.data.total || 0) / limit) || 1);
    } catch (err) {
      toast.error('Failed to load settlement data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettlements();
  }, [page, debouncedSearch, filterStatus, filterInstitution, filterCourse, filterPayoutMode, dateFrom, dateTo]);

  const handleRefreshSettlement = async (id) => {
    setRefreshingId(id);
    try {
      await axios.post(`${apiBase}/settlements/${id}/refresh`);
      toast.success('Settlement status refreshed');
      await fetchSettlements();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to refresh settlement');
    } finally {
      setRefreshingId('');
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        export: 'true'
      };
      if (filterStatus) params.status = filterStatus;
      if (debouncedSearch) params.q = debouncedSearch;
      if (filterInstitution) params.institutionId = filterInstitution;
      if (filterCourse) params.courseId = filterCourse;
      if (filterPayoutMode) params.payoutMode = filterPayoutMode;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      
      const res = await axios.get(`${apiBase}/settlements`, { params });
      const entriesToExport = res.data?.entries || [];
      if (entriesToExport.length === 0) return toast.error('No data to export');

      const headers = isSuperAdmin ? [
        'Institution', 'Transactions', 'Total Collected', 'Platform Income', 'College Share'
      ] : [
        'Application ID', 'Student', 'Institution', 'Total Amount', 
        'Platform Fee', 'College Share', 'Payout Mode', 'Settlement Status', 
        'Payment Date', 'Payment ID', 'Transfer ID', 'Linked Account', 'Settlement UTR', 'Settlement Date'
      ];
      const rows = entriesToExport.map(e => isSuperAdmin ? [
        e.collegeName || '',
        e.transactionCount || 0,
        e.totalAmount || 0,
        e.platformFee || 0,
        e.collegeShare || 0,
      ] : [
        e.applicationId || '',
        e.studentName || '',
        e.collegeName || '',
        e.totalAmount || 0,
        e.platformFee || 0,
        e.collegeShare || 0,
        e.payoutMode || '',
        e.settlementStatus || '',
        e.paidAt ? new Date(e.paidAt).toLocaleString('en-IN') : '',
        e.paymentId || '',
        e.transferId || '',
        hasLinkedRazorpayAccount(e) ? 'Yes' : 'No',
        e.settlementUtr || '',
        e.settlementDate ? new Date(e.settlementDate).toLocaleDateString('en-IN') : ''
      ]);

      const csv = [headers, ...rows].map(row => row.map(value => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `seatify_settlements_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export settlements');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 ${
            page === i
              ? 'z-10 bg-indigo-600 text-white focus-visible:outline-focus-visible:outline-2 focus-visible:outline-indigo-600'
              : 'text-slate-900 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:outline-offset-0'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const filtered = entries;
  const resetFilters = () => {
    setFilterStatus('');
    setFilterInstitution('');
    setFilterCourse('');
    setFilterPayoutMode('');
    setDateFrom('');
    setDateTo('');
  };

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div style={{ zoom: 0.8 }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Poppins' }}>Settlement Ledger</h1>
        <p className="text-slate-500 mt-1">Track Razorpay Route split settlements to linked institution accounts.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 text-slate-400 text-xs font-bold uppercase tracking-wider mb-2">
            <IndianRupee size={14} /> Total Collected
          </div>
          <p className="text-2xl font-extrabold text-slate-800">{formatMoney(summary.totalCollected)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 text-indigo-400 text-xs font-bold uppercase tracking-wider mb-2">
            <ArrowUpRight size={14} /> Platform Revenue
          </div>
          <p className="text-2xl font-extrabold text-indigo-600">{formatMoney(summary.totalPlatformFee)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-wider mb-2">
            <Clock size={14} /> Pending Payouts
          </div>
          <p className="text-2xl font-extrabold text-amber-600">{summary.pendingCount || 0}</p>
          <p className="text-xs text-slate-400 mt-1">{formatMoney(summary.pendingAmount)} outstanding</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2">
            <CheckCircle2 size={14} /> Settled
          </div>
          <p className="text-2xl font-extrabold text-emerald-600">{summary.settledCount || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
        <div className="relative md:col-span-2 xl:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder={isSuperAdmin ? 'Search by institution...' : 'Search by Application ID, Institution, or Student...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
        </div>
        <select
          value={filterInstitution}
          onChange={(e) => { setFilterInstitution(e.target.value); setFilterCourse(''); }}
          className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm font-medium"
        >
          <option value="">All Institutions</option>
          {filterOptions.institutions.map((item) => (
            <option key={item.id || item._id} value={item.id || item._id}>{item.name}</option>
          ))}
        </select>
        <select
          value={filterCourse}
          onChange={(e) => setFilterCourse(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm font-medium"
        >
          <option value="">All Courses</option>
          {filterOptions.courses
            .filter((item) => !filterInstitution || item.institutionId === filterInstitution)
            .map((item) => (
              <option key={item.id || item._id} value={item.id || item._id}>{item.name}</option>
            ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm font-medium"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="settled">Settled</option>
          <option value="failed">Failed</option>
          <option value="not_applicable">Not applicable</option>
        </select>
        <select
          value={filterPayoutMode}
          onChange={(e) => setFilterPayoutMode(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm font-medium"
        >
          <option value="">All Payouts</option>
          <option value="razorpay_route">Razorpay Route</option>
          <option value="normal_razorpay">Normal Razorpay</option>
          <option value="direct_bank">Direct Bank</option>
          <option value="pending_route">Route Pending</option>
        </select>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Paid Start Date</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            aria-label="Paid Start Date"
            className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm font-medium"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Paid End Date</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            aria-label="Paid End Date"
            className="px-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm font-medium"
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          onClick={fetchSettlements}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 rounded-xl outline-none bg-white text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw size={16} />
          Refresh Live
        </button>
        <button
          onClick={resetFilters}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 rounded-xl outline-none bg-white text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          Clear Filters
        </button>
        <button
          onClick={handleExport}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-bold shadow-sm transition-all"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>
      {canRefreshSettlements && (
        <div className={`mb-4 text-xs font-bold ${liveRefresh ? 'text-emerald-600' : 'text-amber-600'}`}>
          {liveRefresh ? 'Live Razorpay refresh enabled' : 'Showing saved data. Configure Razorpay credentials to refresh live settlement status.'}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Institution</th>
                <th className="text-left px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">{isSuperAdmin ? 'Transactions' : 'Student'}</th>
                {!isSuperAdmin && <th className="text-left px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Application</th>}
                {!isSuperAdmin && <th className="text-left px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Paid Date</th>}
                <th className="text-right px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Total</th>
                <th className="text-right px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Platform</th>
                <th className="text-right px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Institution Share</th>
                {!isSuperAdmin && <th className="text-center px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Payout Mode</th>}
                {!isSuperAdmin && <th className="text-center px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Status</th>}
                {!isSuperAdmin && <th className="text-left px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Razorpay Transfer</th>}
                {!isSuperAdmin && <th className="text-left px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Settlement Date</th>}
                {!isSuperAdmin && <th className="text-center px-5 py-3.5 font-bold text-slate-500 uppercase tracking-wider text-xs">Action</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-12 text-slate-400">
                    <div className="flex flex-col items-center gap-2">
                      <Building size={32} className="text-slate-300" />
                      <p>No settlement entries found.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map(entry => {
                  const sc = statusConfig[entry.settlementStatus] || statusConfig.pending;
                  const isNormalRazorpay = entry.payoutMode === 'normal_razorpay' || entry.settlementStatus === 'not_applicable';
                  return (
                    <tr key={entry._id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4 font-medium text-slate-700 max-w-[160px] truncate">{entry.collegeName}</td>
                      <td className="px-5 py-4 text-slate-600">{isSuperAdmin ? entry.transactionCount : entry.studentName}</td>
                      {!isSuperAdmin && <td className="px-5 py-4 font-mono text-xs font-bold text-indigo-600">{entry.applicationId}</td>}
                      {!isSuperAdmin && <td className="px-5 py-4 text-xs text-slate-600 min-w-[145px]">{formatDate(entry.paidAt)}</td>}
                      <td className="px-5 py-4 text-right font-bold text-slate-800">{formatMoney(entry.totalAmount)}</td>
                      <td className="px-5 py-4 text-right font-semibold text-indigo-500">{formatMoney(entry.platformFee)}</td>
                      <td className="px-5 py-4 text-right font-bold text-emerald-600">{formatMoney(entry.collegeShare)}</td>
                      {!isSuperAdmin && <td className="px-5 py-4 text-center">
                        <span className="text-xs font-bold bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">
                          {modeLabels[entry.payoutMode] || entry.payoutMode}
                        </span>
                      </td>}
                      {!isSuperAdmin && <td className="px-5 py-4 text-center">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-lg border ${sc.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                          {sc.label}
                        </span>
                        {entry.liveError ? <p className="text-[11px] text-amber-600 mt-1">{entry.liveError}</p> : null}
                      </td>}
                      {!isSuperAdmin && <td className="px-5 py-4 min-w-[220px]">
                        <p className="font-mono text-xs text-slate-500">Pay: {entry.paymentId || 'N/A'}</p>
                        <p className="font-mono text-xs font-bold text-slate-800 mt-1">Tr: {entry.transferId || (isNormalRazorpay ? 'Not applicable' : 'Not created yet')}</p>
                        <p className="font-mono text-[11px] text-slate-400 mt-1">
                          Linked: {hasLinkedRazorpayAccount(entry) ? 'Yes' : 'No'}
                          {entry.linkedAccountId ? ` (${entry.linkedAccountId})` : ''}
                        </p>
                        {entry.settlementId ? <p className="font-mono text-[11px] text-slate-400 mt-1">Setl: {entry.settlementId}</p> : null}
                        {entry.settlementUtr ? <p className="font-mono text-[11px] text-emerald-600 mt-1">UTR: {entry.settlementUtr}</p> : null}
                      </td>}
                      {!isSuperAdmin && <td className="px-5 py-4 text-xs text-slate-600 min-w-[150px]">
                        <p className="font-semibold text-slate-700">{formatDate(entry.settlementDate)}</p>
                        <p className="text-slate-400 mt-1">Checked: {formatDate(entry.transferCheckedAt)}</p>
                      </td>}
                      {!isSuperAdmin && <td className="px-5 py-4 text-center">
                        {canRefreshSettlements && !isNormalRazorpay ? (
                          <button
                            onClick={() => handleRefreshSettlement(entry._id)}
                            disabled={refreshingId === entry._id}
                            className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 rounded-lg transition-all shadow-sm"
                          >
                            <RefreshCw size={14} className={refreshingId === entry._id ? 'animate-spin' : ''} />
                            Check
                          </button>
                        ) : (
                          <span className="text-xs font-bold text-slate-400">View only</span>
                        )}
                      </td>}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6 mt-4 rounded-2xl shadow-sm">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="relative ml-3 inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing <span className="font-bold">{(page - 1) * limit + 1}</span> to{' '}
                <span className="font-bold">{Math.min(page * limit, total)}</span> of{' '}
                <span className="font-bold">{total}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-xl shadow-sm bg-white border border-slate-200 overflow-hidden" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-3 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50 font-bold border-r border-slate-200"
                >
                  &larr;
                </button>
                {renderPageNumbers()}
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="relative inline-flex items-center px-3 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50 font-bold border-l border-slate-200"
                >
                  &rarr;
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
